import { useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useLang, tx } from "@/context/LangContext";
import { X } from "lucide-react";
import ShareMediaButton from "@/components/ShareMediaButton";

const EASE = [0.16, 1, 0.3, 1];

export default function ProjectModal({ project, onClose }) {
    const { lang } = useLang();

    useEffect(() => {
        window.__lenis?.stop?.();
        const onKey = (e) => e.key === "Escape" && onClose();
        window.addEventListener("keydown", onKey);
        return () => {
            window.__lenis?.start?.();
            window.removeEventListener("keydown", onKey);
        };
    }, [onClose]);

    return (
        <motion.div
            data-testid="project-modal"
            className="fixed inset-0 z-[80] flex items-center justify-center bg-ink/95 p-4 backdrop-blur-md md:p-10"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            onClick={onClose}
        >
            <motion.div
                onClick={(e) => e.stopPropagation()}
                initial={{ y: 70, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: 40, opacity: 0 }}
                transition={{ duration: 0.6, ease: EASE }}
                className="relative grid max-h-[92vh] w-full max-w-6xl overflow-hidden border border-white/10 bg-ink2 md:grid-cols-5"
            >
                <button
                    data-testid="project-modal-close"
                    onClick={onClose}
                    className="absolute right-4 top-4 z-10 flex h-11 w-11 items-center justify-center rounded-full border border-white/20 bg-ink/60 text-cream transition-colors hover:bg-violet hover:border-violet"
                    aria-label="Close"
                >
                    <X size={16} />
                </button>

                <div className="md:col-span-3">
                    {project.youtubeId ? (
                        <iframe
                            data-testid="project-modal-youtube"
                            src={`https://www.youtube-nocookie.com/embed/${project.youtubeId}?autoplay=1&rel=0`}
                            title={project.title}
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                            allowFullScreen
                            className="h-64 w-full md:h-full"
                        />
                    ) : project.video ? (
                        <video
                            data-testid="project-modal-video"
                            className="h-64 w-full object-cover md:h-full"
                            poster={project.poster || project.img}
                            autoPlay
                            muted
                            loop
                            playsInline
                            controls
                            onCanPlay={(e) => {
                                e.currentTarget.muted = true;
                                e.currentTarget.play().catch(() => {});
                            }}
                        >
                            <source src={project.video} type="video/mp4" />
                            <source src={`${project.video.split(".mp4")[0]}.webm`} type="video/webm" />
                        </video>
                    ) : (
                        <img
                            src={project.img}
                            alt={project.title}
                            className="h-64 w-full object-cover md:h-full"
                        />
                    )}
                </div>

                <div className="flex flex-col justify-between p-7 md:col-span-2 md:p-10">
                    <div>
                        <div className="flex flex-wrap items-center gap-3 font-mono text-[10px] tracking-[0.3em] text-violet">
                            {project.category}
                            <span className="text-cream/40">/ {project.year}</span>
                        </div>
                        <h3 className="mt-5 font-display text-3xl font-extrabold uppercase leading-none tracking-tight text-cream md:text-4xl">
                            {project.title}
                        </h3>
                        <p className="mt-3 font-mono text-[11px] tracking-[0.25em] text-cream/60">
                            {project.client}
                        </p>
                        {/* partage story (vidéos locales : GIF/clip filigrané ; photos : image filigranée) */}
                        {(project.video || project.img) && !project.youtubeId && (
                            <div className="mt-5">
                                <ShareMediaButton
                                    kind={project.video ? "video" : "photo"}
                                    src={project.video || project.img}
                                    poster={project.poster || project.img}
                                    title={`${project.title}${project.client ? ` — ${project.client}` : ""}`}
                                    target="#work"
                                    testid="share-project-modal"
                                />
                            </div>
                        )}
                        {project.description ? (
                            <p
                                data-testid="project-modal-description"
                                className="mt-6 text-sm font-light leading-relaxed text-cream/70"
                            >
                                {project.description}
                            </p>
                        ) : project.desc ? (
                            <p className="mt-6 text-sm font-light leading-relaxed text-cream/70">
                                {tx(lang, project.desc)}
                            </p>
                        ) : null}
                    </div>
                    <div className="mt-8 space-y-0 border-t border-white/10 pt-4 font-mono text-[10px] tracking-[0.2em]">
                        <div className="flex justify-between py-2 text-cream/50">
                            <span>RÉALISATION</span>
                            <span className="text-cream">CASPER</span>
                        </div>
                        <div className="flex justify-between py-2 text-cream/50">
                            <span>PRODUCTION</span>
                            <span className="text-cream">GLOOM ENT.</span>
                        </div>
                        <div className="flex justify-between py-2 text-cream/50">
                            <span>DURÉE / PÉRIMÈTRE</span>
                            <span className="text-cream">{project.duration || "—"}</span>
                        </div>
                        {project.views != null && (
                            <div className="flex justify-between py-2 text-cream/50">
                                <span>{lang === "fr" ? "VUES YOUTUBE" : "YOUTUBE VIEWS"}</span>
                                <span className="text-cream">
                                    {project.views.toLocaleString(lang === "fr" ? "fr-FR" : "en-US")}
                                </span>
                            </div>
                        )}
                        {project.url && (
                            <div className="flex justify-between py-2 text-cream/50">
                                <span>SOURCE</span>
                                <a
                                    href={project.url}
                                    target="_blank"
                                    rel="noreferrer"
                                    data-testid="project-modal-youtube-link"
                                    className="text-cream underline-offset-4 transition-colors hover:text-violet hover:underline"
                                >
                                    YOUTUBE
                                </a>
                            </div>
                        )}
                    </div>
                </div>
            </motion.div>
        </motion.div>
    );
}
